/**
 * Store managing state for Course Authoring
 */
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'vend... Remove this comment to see the full error message
import createStore from 'vendor/cnpm/fluxible.v0-4/addons/createStore';

import CourseMaterial from 'bundles/author-course/models/CourseMaterial';
import type Item from 'bundles/author-course/models/Item';
import type Lesson from 'bundles/author-course/models/Lesson';
import type Module from 'bundles/author-course/models/Module';
import type { ItemTypeLabel } from 'bundles/item/types/ItemType';

const SERIALIZED_PROPS = [
  'courseMaterialsByBranchId',
  'loadedCourseMaterialForBranchId',
  'changeScopeByBranchId',
  'contextData',
  'draftsByBranchId',
  'authoringAtomsByBranchId',
  'authoringAtomRelationsByBranchId',
  'isLoading',
];

// TODO: This is incomplete workaround to type CourseMaterialStore. Need to create type
// definitions for createStore and finalize this one.
export type CourseMaterialStoreType = {
  handlers: any;
  getCourseMaterial: (branchId: string) => CourseMaterial;
  getLessonForItem: (branchId: string, itemId: string) => Lesson;
  getModuleForItem: (branchId: string, itemId: string) => Module;
  getItemMetadataForItemId: (branchId: string, itemId: string) => Item;
  updateDraftByBranchAndItemId: (branchId: string, itemId: string, newName: string) => void;
  isStoreLoading: () => boolean;
};

const CourseMaterialStore: CourseMaterialStoreType = createStore({
  storeName: 'CourseMaterialStore',

  initialize() {
    this.courseMaterialsByBranchId = {};
    this.loadedCourseMaterialForBranchId = {};
    this.changeScopeByBranchId = {};
    this.contextData = {};
    this.draftsByBranchId = {};
    this.authoringAtomsByBranchId = {};
    this.authoringAtomRelationsByBranchId = {};
    this.isLoading = false;
  },

  dehydrate() {
    return pick(this, ...SERIALIZED_PROPS);
  },

  rehydrate(state: $TSFixMe) {
    Object.assign(this, pick(state, ...SERIALIZED_PROPS));

    // recreate es6 model on client
    this.courseMaterialsByBranchId = Object.entries(
      state.courseMaterialsByBranchId as Record<string, CourseMaterial>
    ).reduce((materials, [branchId, material]) => {
      // recreate the inputs for the courseMaterial model

      const {
        elements,
        gradePolicy,
        gradingParameters,
        elementsRequiredForPassing,
        learningObjectives,
        itemToLearningObjectives,
      } = material;
      const { itemLocks, premiumItemLocks } = material.context;
      const materialInput = {
        branchId,
        courseMaterial: {
          itemLocks,
          premiumItemLocks,
          elements,
          gradePolicy,
          gradingParameters,
          elementsRequiredForPassing,
          learningObjectives,
          itemToLearningObjectives,
        },
        changeScope: state.changeScopeByBranchId[branchId],
        drafts: state.draftsByBranchId[branchId],
        ...material.context,
        authoringAtoms: state.authoringAtomsByBranchId[branchId],
        authoringAtomRelations: state.authoringAtomRelationsByBranchId[branchId],
      };

      return { ...materials, [branchId]: new CourseMaterial(materialInput) };
    }, {});
  },

  hasLoaded(branchId: string) {
    return !!this.loadedCourseMaterialForBranchId[branchId];
  },

  hasLoadedDraftsForBranch(branchId: string) {
    return !!this.draftsByBranchId[branchId];
  },
  /**
   * Return a branch by branch id.
   * @param  {string} branchId - some branchId.
   * @return {CourseMaterial}
   */
  getCourseMaterial(branchId: string): CourseMaterial | null {
    return this.courseMaterialsByBranchId[branchId] || null;
  },

  getModules(branchId: string) {
    const courseMaterial = this.getCourseMaterial(branchId) as CourseMaterial | null;
    return courseMaterial?.modules;
  },

  getModuleForItem(branchId: string, itemId: string) {
    const courseMaterial = this.getCourseMaterial(branchId) as CourseMaterial | null;
    if (!courseMaterial) {
      return null;
    }

    const item = courseMaterial.items.find((potentialItem) => potentialItem.id === itemId);
    const moduleId = item?.moduleId;
    return courseMaterial.modules.find((module) => module.id === moduleId);
  },

  getLessonForItem(branchId: string, itemId: string) {
    const courseMaterial = this.getCourseMaterial(branchId) as CourseMaterial | null;

    if (!courseMaterial) {
      return null;
    }

    return courseMaterial.lessons.find((lesson) => lesson.items.find((item) => item.id === itemId));
  },

  getParentIdsForItems(branchId: $TSFixMe, itemIds: $TSFixMe) {
    const courseMaterial = this.getCourseMaterial(branchId);

    if (!courseMaterial) {
      return {};
    }

    return courseMaterial.getParentIdsForItems(itemIds);
  },

  getParentIdsForItem(branchId: string, itemId: string) {
    const courseMaterial = this.getCourseMaterial(branchId) as CourseMaterial | null;

    if (!courseMaterial) {
      return {};
    }

    const parentIdsForItem = courseMaterial.getParentIdsForItems([itemId]);

    return parentIdsForItem[itemId];
  },

  getChangeScope(branchId: string) {
    return this.changeScopeByBranchId[branchId] || null;
  },

  getItemLocks(branchId: string) {
    return (this.getCourseMaterial(branchId) as CourseMaterial | null)?.context.itemLocks;
  },

  getPremiumItemLocks(branchId: string) {
    const courseMaterial = this.getCourseMaterial(branchId) as CourseMaterial | null;
    return courseMaterial?.context.premiumItemLocks;
  },

  getItemMetadataForItemId(branchId: string, itemId: string): Item | undefined {
    const courseMaterial = this.getCourseMaterial(branchId) as CourseMaterial | null;
    const item = courseMaterial?.items.find((elem) => elem.id === itemId);

    return item;
  },

  isStoreLoading(): boolean {
    return this.isLoading;
  },
});

CourseMaterialStore.handlers = {
  SET_CONTEXT_INFO({
    courseSlug,
    courseId,
    branchId,
    isBranchLaunched,
    isBranchPrivate,
    isBreakingChangeAllowed,
  }: $TSFixMe) {
    if (courseSlug) {
      this.contextData.courseSlug = courseSlug;
    }
    if (courseId) {
      this.contextData.courseId = courseId;
    }
    if (isBranchLaunched !== undefined) {
      this.contextData.isBranchLaunched = isBranchLaunched;
    }
    if (isBranchPrivate !== undefined) {
      this.contextData.isBranchPrivate = isBranchPrivate;
    }

    if (isBreakingChangeAllowed !== undefined) {
      this.contextData.isBreakingChangeAllowed = isBreakingChangeAllowed;
    }

    // update context data for current course material if loaded
    if (this.loadedCourseMaterialForBranchId[branchId]) {
      const courseMaterial = this.courseMaterialsByBranchId[branchId];

      courseMaterial.context = Object.assign({}, courseMaterial.context, this.contextData);
    }

    this.emitChange();
  },

  SAVE_DRAFTS_FOR_BRANCH({ branchId, drafts }: { branchId: string; drafts: $TSFixMe }) {
    // merge the old and new draft item objects
    // this allows us to see which items have their draft status changed so we know which items to update in the model
    // e.g. draft --> published and published --> draft

    const oldItemsWithDrafts = mapValues({ ...this.draftsByBranchId[branchId] }, () => undefined);

    const itemsWithDraftStatusChanges = Object.assign(oldItemsWithDrafts, drafts);

    this.draftsByBranchId[branchId] = drafts;
    if (this.courseMaterialsByBranchId[branchId]) {
      // brief workaround: get the parent ids of each draft item id, then update drafts
      // drafts are keyed by ids

      const draftIds = Object.keys(itemsWithDraftStatusChanges);
      const draftsWithParentIds = this.courseMaterialsByBranchId[branchId].getParentIdsForItems(draftIds);
      this.courseMaterialsByBranchId[branchId].updateDrafts(itemsWithDraftStatusChanges, draftsWithParentIds);
      this.emitChange();
    }
  },

  UPDATE_DRAFT_FOR_BRANCH_AND_ITEMID({
    branchId,
    itemId,
    newName,
    newItemTypeLabel,
  }: {
    branchId: string;
    itemId: string;
    newName?: string;
    newItemTypeLabel?: ItemTypeLabel;
  }) {
    if (newName) {
      this.draftsByBranchId[branchId][itemId] = {
        draftName: newName,
      };
    }

    if (newItemTypeLabel) {
      this.draftsByBranchId[branchId][itemId] = {
        itemTypeLabel: newItemTypeLabel,
      };
    }
  },

  LOADED_COURSE_MATERIAL_FOR_BRANCH({
    branchId,
    courseMaterial,
    changeScope,
    authoringAtoms,
    authoringAtomRelations,
  }: $TSFixMe) {
    const drafts = this.draftsByBranchId[branchId];

    if (authoringAtoms) {
      // when course material is reloaded via authoringItemContents.v1 we cant get the linked authoringAtoms resource
      // so only replace authoringAtoms if it actually has data
      this.authoringAtomsByBranchId[branchId] = authoringAtoms;
    }
    if (authoringAtomRelations) {
      this.authoringAtomRelationsByBranchId[branchId] = authoringAtomRelations;
    }

    this.courseMaterialsByBranchId[branchId] = new CourseMaterial(
      Object.assign(
        {
          branchId,
          courseMaterial,
          changeScope,
          drafts,
          authoringAtoms: this.authoringAtomsByBranchId[branchId],
          authoringAtomRelations: this.authoringAtomRelationsByBranchId[branchId],
        },
        this.contextData
      )
    );
    this.loadedCourseMaterialForBranchId[branchId] = true;
    this.changeScopeByBranchId[branchId] = changeScope;

    this.emitChange();
  },

  // clears out all loaded courseMaterial execept for one branchId, used for clearing out loadedCourseMaterials when freezing an atom across branches in the same course. This will force the courseMaterial to reload with the correct frozen atom before a user sees it again
  CLEAR_COURSE_MATERIAL_FOR_OTHER_BRANCHES({ branchId }: { branchId: string }) {
    for (const loadedBranchId of Object.keys(this.loadedCourseMaterialForBranchId)) {
      if (loadedBranchId !== branchId) {
        delete this.loadedCourseMaterialForBranchId[loadedBranchId];
        delete this.courseMaterialsByBranchId[loadedBranchId];
      }
    }
  },

  CHANGE_GRADE_POLICY({ branchId, gradePolicy }: { branchId: string; gradePolicy: $TSFixMe }) {
    this.courseMaterialsByBranchId[branchId].gradePolicy = gradePolicy;
  },

  CHANGE_DISPLAY_CURRENT_GRADE({ branchId, displayCurrentGrade }: { branchId: string; displayCurrentGrade: boolean }) {
    this.courseMaterialsByBranchId[branchId].gradingParameters.displayCurrentGrade = displayCurrentGrade;

    this.emitChange();
  },

  SET_LOADING_STATE({ isLoading }: { isLoading: boolean }) {
    this.isLoading = !!isLoading;
  },
};

export default CourseMaterialStore;
